import { gql, useLazyQuery, useMutation, useQuery } from '@apollo/client';
import type { ColDef } from 'ag-grid-community';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';

import {
	ActiveInactiveCellRenderer,
	DeleteCellRenderer
} from '@/components/grid';
import { ListPageLayout } from '@/components/layout/list-page-layout';
import { ClearFiltersButton } from '@/components/ui/clear-filters-button';
import { useConfirmationDialog } from '@/components/ui/confirmation-dialog';
import { SentryDropdown } from '@/components/ui/sentry-dropdown';
import type {
	PricingPoliciesQuery,
	PricingPolicyQuery
} from '@/graphql/generated';
import { useGo, useResourceNavigation } from '@/hooks/useResourceNavigation';
import { extractCheckResultError } from '@/lib/checkResult';
import { DEFAULT_PAGE_SIZE } from '@/lib/constants';
import { showMutationSuccessToast } from '@/lib/mutationRefetch';
import { openEntityInNewWindow } from '@/lib/open-in-new-window';
import { showDeleteColumn } from '@/lib/featureFlags';

import { List, Tag } from 'lucide-react';
import {
	PRICING_POLICIES_LIST_QUERY,
	PRICING_POLICY_DELETE_MUTATION,
	PRICING_POLICY_QUERY
} from './pricing-policies.gql.tsx';
import { PricingPolicyDetails, PricingPolicyDetailsHeader } from './pricing-policy-details';

type PricingPolicyListNode = NonNullable<
	NonNullable<PricingPoliciesQuery['pricingPolicies']>['nodes']
>[number];

interface PricingPoliciesListResponse {
	pricingPolicies?: {
		nodes?: PricingPolicyListNode[] | null;
		pageInfo?: {
			hasNextPage: boolean;
			endCursor?: string | null;
		} | null;
	} | null;
}

interface TariffFilterOptionsResponse {
	tariffs?: {
		pageInfo?: {
			hasNextPage: boolean;
			endCursor?: string | null;
		} | null;
		edges?: Array<{
			node?: {
				tariffId: string;
				name?: string | null;
			} | null;
		}> | null;
		nodes?: Array<{
			tariffId: string;
			name?: string | null;
		}> | null;
	} | null;
}

const extractTariffNodes = (
	data: TariffFilterOptionsResponse | undefined
): Array<{ tariffId: string; name?: string | null }> => {
	const nodes = data?.tariffs?.nodes ?? [];
	const edgeNodes = (data?.tariffs?.edges ?? [])
		.map(edge => edge?.node)
		.filter(
			(node): node is { tariffId: string; name?: string | null } =>
				Boolean(node?.tariffId)
		);
	return [...nodes, ...edgeNodes];
};

const TARIFF_FILTER_OPTIONS_QUERY = gql`
	query PricingPoliciesTariffFilterOptions(
		$where: TariffViewModelFilterInput
		$first: Int
		$after: String
	) {
		tariffs(where: $where, first: $first, after: $after) {
			edges {
				node {
					tariffId
					name
				}
			}
			nodes {
				tariffId
				name
			}
			pageInfo {
				hasNextPage
				endCursor
			}
		}
	}
`;

const TARIFF_FILTER_ALL = '';

export const PricingPoliciesList = () => {
	const { t } = useTranslation('pricingPolicies');
	const [selectedPolicy, setSelectedPolicy] =
		useState<PricingPolicyListNode | null>(null);
	const [localPolicies, setLocalPolicies] = useState<PricingPolicyListNode[]>(
		[]
	);
	const { showDialog } = useConfirmationDialog();
	const [searchParams, setSearchParams] = useSearchParams();
	const { goToCreate, goToEdit } = useResourceNavigation('pricingPolicies');
	const go = useGo();

	// Track total items to fetch (for preserving count after create/edit refetch)
	const [itemsToFetch, setItemsToFetch] = useState(DEFAULT_PAGE_SIZE);
	const [selectedTariffId, setSelectedTariffId] = useState(
		TARIFF_FILTER_ALL
	);
	const [searchValue, setSearchValue] = useState('');
	const [clearSearchToken, setClearSearchToken] = useState(0);

	const hasActiveFilters = useMemo(
		() =>
			selectedTariffId !== TARIFF_FILTER_ALL ||
			searchValue.trim().length > 0,
		[selectedTariffId, searchValue]
	);

	const queryVariables = useMemo(() => {
		if (!selectedTariffId) {
			return {
				first: itemsToFetch
			};
		}

		return {
			first: itemsToFetch,
			where: {
				tariffId: {
					eq: selectedTariffId
				}
			}
		};
	}, [itemsToFetch, selectedTariffId]);

	const { data, loading: isLoading, refetch, fetchMore } =
		useQuery<PricingPoliciesListResponse>(PRICING_POLICIES_LIST_QUERY, {
			variables: queryVariables
		});

	const { data: tariffFilterOptionsData } =
		useQuery<TariffFilterOptionsResponse>(TARIFF_FILTER_OPTIONS_QUERY, {
			variables: {
				first: DEFAULT_PAGE_SIZE
			}
		});

	// Pagination state
	const [isLoadingMore, setIsLoadingMore] = useState(false);
	const hasNextPage = data?.pricingPolicies?.pageInfo?.hasNextPage ?? false;
	const endCursor = data?.pricingPolicies?.pageInfo?.endCursor;

	// Load more handler using fetchMore
	const handleLoadMore = useCallback(async () => {
		if (!hasNextPage || isLoadingMore || !endCursor) return;

		setIsLoadingMore(true);
		try {
			await fetchMore({
				variables: {
					...(selectedTariffId
						? {
								where: {
									tariffId: {
										eq: selectedTariffId
									}
								}
							}
						: {}),
					first: DEFAULT_PAGE_SIZE,
					after: endCursor
				},
				updateQuery: (prev, { fetchMoreResult }) => {
					if (!fetchMoreResult?.pricingPolicies) return prev;

					return {
						...prev,
						pricingPolicies: {
							...fetchMoreResult.pricingPolicies,
							nodes: [
								...(prev.pricingPolicies?.nodes ?? []),
								...(fetchMoreResult.pricingPolicies.nodes ?? [])
							]
						}
					};
				}
			});
			// Reset local policies to use server data after fetchMore
			setLocalPolicies([]);
		} catch (error) {
			console.error('Error loading more pricing policies:', error);
		} finally {
			setIsLoadingMore(false);
		}
	}, [fetchMore, hasNextPage, isLoadingMore, endCursor, selectedTariffId]);

	const pricingPoliciesData = data?.pricingPolicies?.nodes ?? [];

	const handleRefresh = () => {
		setLocalPolicies([]);
		setItemsToFetch(DEFAULT_PAGE_SIZE);
		refetch({
			...(selectedTariffId
				? {
						where: {
							tariffId: {
								eq: selectedTariffId
							}
						}
					}
				: {}),
			first: DEFAULT_PAGE_SIZE
		});
	};

	const handleClearFilters = () => {
		setSelectedTariffId(TARIFF_FILTER_ALL);
		setClearSearchToken(token => token + 1);
	};

	// Lazy query for fetching individual pricing policy after success
	const [fetchPricingPolicy] =
		useLazyQuery<PricingPolicyQuery>(PRICING_POLICY_QUERY);

	// Delete mutation
	const [deletePricingPolicy, { loading: deleteLoading }] = useMutation(
		PRICING_POLICY_DELETE_MUTATION
	);

	// Combined data: server data + local modifications
	const allPolicies = useMemo(() => {
		const serverPolicies = pricingPoliciesData || [];

		// Always prefer server data when empty - local state is only for optimistic updates
		if (localPolicies.length === 0 || serverPolicies.length === 0) {
			return serverPolicies;
		}

		// Merge local policies with server data
		const mergedPolicies = [...serverPolicies];

		for (const localPolicy of localPolicies) {
			const existingIndex = mergedPolicies.findIndex(
				policy => policy.pricingPolicyId === localPolicy.pricingPolicyId
			);

			if (existingIndex >= 0) {
				// Replace existing (for edits)
				mergedPolicies[existingIndex] = localPolicy;
			} else {
				// Add new (for creates)
				mergedPolicies.unshift(localPolicy);
			}
		}

		return mergedPolicies;
	}, [pricingPoliciesData, localPolicies]);

	const visibleTariffIds = useMemo(
		() =>
			[...new Set(allPolicies.map(policy => policy.tariffId).filter(Boolean))],
		[allPolicies]
	);

	const { data: visibleTariffOptionsData } =
		useQuery<TariffFilterOptionsResponse>(TARIFF_FILTER_OPTIONS_QUERY, {
			variables: {
				where: {
					tariffId: {
						in: visibleTariffIds
					}
				},
				first: Math.max(
					1,
					Math.min(visibleTariffIds.length, DEFAULT_PAGE_SIZE)
				)
			},
			skip: visibleTariffIds.length === 0
		});

	const allKnownTariffs = useMemo(() => {
		const map = new Map<string, { tariffId: string; name?: string | null }>();
		const baseTariffs = extractTariffNodes(tariffFilterOptionsData);
		for (const tariff of baseTariffs) {
			map.set(tariff.tariffId, tariff);
		}
		const visibleTariffs = extractTariffNodes(visibleTariffOptionsData);
		for (const tariff of visibleTariffs) {
			map.set(tariff.tariffId, tariff);
		}
		return [...map.values()];
	}, [tariffFilterOptionsData, visibleTariffOptionsData]);

	const tariffNameById = useMemo(() => {
		const map = new Map<string, string>();
		for (const tariff of allKnownTariffs) {
			map.set(tariff.tariffId, tariff.name ?? '—');
		}
		return map;
	}, [allKnownTariffs]);

	const tariffFilterOptions = useMemo(
		() => [
			{
				value: TARIFF_FILTER_ALL,
				label: t('list.filters.allTariffs', 'All tariffs'),
				icon: <List className="w-4 h-4 text-gray-500" />
			},
			...[...allKnownTariffs]
				.sort((a, b) =>
					(a.name ?? '').localeCompare(b.name ?? '', undefined, {
						sensitivity: 'base'
					})
				)
				.map(tariff => ({
					value: tariff.tariffId,
					label: tariff.name ?? '—',
					icon: <Tag className="w-4 h-4 text-gray-500" />
				}))
		],
		[t, allKnownTariffs]
	);

	useEffect(() => {
		setLocalPolicies([]);
		setItemsToFetch(DEFAULT_PAGE_SIZE);
	}, [selectedTariffId]);

	useEffect(() => {
		if (
			selectedPolicy &&
			!allPolicies.some(
				policy => policy.pricingPolicyId === selectedPolicy.pricingPolicyId
			)
		) {
			setSelectedPolicy(null);
		}
	}, [allPolicies, selectedPolicy]);

	// Handle success from create/edit operations
	const handlePolicySuccess = useCallback(
		async (pricingPolicyId: string) => {
			try {
				// Fetch the updated/created pricing policy from server
				const { data: policyData } = await fetchPricingPolicy({
					variables: { id: pricingPolicyId },
					fetchPolicy: 'network-only' // Always fetch fresh data
				});

				const policyFromServer = policyData?.pricingPolicy;
				if (!policyFromServer) {
					console.warn(
						'Could not fetch pricing policy data after success'
					);
					return;
				}

				// Map the detailed policy data to list format
				const listPolicy: PricingPolicyListNode = {
					pricingPolicyId: policyFromServer.pricingPolicyId,
					name: policyFromServer.name,
					priority: policyFromServer.priority,
					isActive: policyFromServer.isActive,
					tariffId: policyFromServer.tariffId,
					scenario: policyFromServer.scenario,
					rate: policyFromServer.rate,
					overstayRate: policyFromServer.overstayRate,
					limit: policyFromServer.limit,
					billingCycle: policyFromServer.billingCycle
				};

				// Update local policies
				setLocalPolicies(prevLocal => {
					const existingIndex = prevLocal.findIndex(
						policy => policy.pricingPolicyId === pricingPolicyId
					);

					if (existingIndex >= 0) {
						// Replace existing policy (edit case)
						const updated = [...prevLocal];
						updated[existingIndex] = listPolicy;
						return updated;
					} else {
						// Add new policy (create case)
						return [listPolicy, ...prevLocal];
					}
				});

				// Select and highlight the policy
				setSelectedPolicy(listPolicy);
			} catch (error) {
				console.error(
					'Error fetching pricing policy after success:',
					error
				);
			}
		},
		[fetchPricingPolicy]
	);

	// URL parameter handling for success operations
	useEffect(() => {
		const successPolicyId = searchParams.get('success');

		if (successPolicyId) {
			// Clear the URL parameters
			setSearchParams(params => {
				params.delete('success');
				params.delete('mode');
				return params;
			});

			// Handle the success
			handlePolicySuccess(successPolicyId);
		}
	}, [searchParams, setSearchParams, handlePolicySuccess]);

	const handleRowClick = (policy: PricingPolicyListNode) => {
		setSelectedPolicy(policy);
	};

	// Helper function to find the next pricing policy to select after deletion
	const findNextPolicyToSelect = (
		deletedPolicy: PricingPolicyListNode,
		policyList: PricingPolicyListNode[]
	) => {
		const currentIndex = policyList.findIndex(
			p => p.pricingPolicyId === deletedPolicy.pricingPolicyId
		);

		if (policyList.length <= 1) {
			return null;
		}

		// Try to select the next policy (same index after deletion)
		if (currentIndex < policyList.length - 1) {
			return policyList[currentIndex + 1];
		}

		// If deleting the last policy, select the previous one
		if (currentIndex > 0) {
			return policyList[currentIndex - 1];
		}

		return null;
	};

	// Helper function to handle post-deletion selection
	const handlePostDeletionSelection = (
		nextPolicyToSelect: PricingPolicyListNode | null,
		updatedPolicies: PricingPolicyListNode[]
	) => {
		setLocalPolicies([]);
		// First, clear the current selection completely
		setSelectedPolicy(null);

		// Then after a brief delay, set the next policy
		setTimeout(() => {
			if (nextPolicyToSelect && updatedPolicies.length > 0) {
				// Find the policy in the updated list by ID
				const updatedPolicy = updatedPolicies.find(
					p => p.pricingPolicyId === nextPolicyToSelect?.pricingPolicyId
				);

				if (updatedPolicy) {
					setSelectedPolicy(updatedPolicy);
				}
			}
		}, 100);
	};

	const handleDeletePricingPolicy = async (policy: PricingPolicyListNode) => {
		const confirmed = await showDialog(
			'Delete Pricing Policy',
			`Are you sure you want to delete pricing policy "${policy.name}"? This action cannot be undone.`
		);

		if (confirmed) {
			// Determine if we need to find the next policy to select
			const wasSelectedPolicy =
				selectedPolicy?.pricingPolicyId === policy.pricingPolicyId;

			// Clear selection immediately if deleting the selected policy
			if (wasSelectedPolicy) {
				setSelectedPolicy(null);
			}

			try {
				const result = await deletePricingPolicy({
					variables: {
						input: {
							pricingPolicyId: policy.pricingPolicyId
						}
					},
					refetchQueries: ['PricingPolicies']
				});

				const checkResult = result.data?.deletePricingPolicy;

				if (checkResult?.success) {
					showMutationSuccessToast('pricingPolicies', 'delete');

					// Find the next policy to select after deletion (before refetch)
					let nextPolicyToSelect = null;
					if (wasSelectedPolicy) {
						nextPolicyToSelect = findNextPolicyToSelect(
							policy,
							allPolicies
						);
					}

					// Refetch data
					refetch();

					// Wait a bit for the refresh to complete, then handle selection
					setTimeout(() => {
						handlePostDeletionSelection(
							nextPolicyToSelect,
							pricingPoliciesData || []
						);
					}, 200);
				} else {
					const errorMessage = extractCheckResultError(checkResult);
					toast.error(
						`Failed to delete pricing policy: ${errorMessage}`
					);
				}
			} catch (error) {
				console.error('Delete pricing policy error:', error);
				toast.error('Failed to delete pricing policy. Please try again.');
			}
		}
	};

	const handleDeletePricingPolicyById = async (pricingPolicyId: string) => {
		const policy = pricingPoliciesData?.find(
			p => p.pricingPolicyId === pricingPolicyId
		);
		if (policy) {
			await handleDeletePricingPolicy(policy);
		}
	};

	const handleClonePricingPolicy = (pricingPolicyId: string) => {
		const policy = pricingPoliciesData?.find(
			p => p.pricingPolicyId === pricingPolicyId
		);
		if (policy) {
			go({
				to: '/pricing-policies/create',
				state: {
					cloneData: {
						// Core fields
						name: `${policy.name} (Copy)`,
						priority: policy.priority,
						isActive: policy.isActive,
						// Foreign keys with their labels for autocomplete display
						scenarioId: policy.scenario?.scenarioId,
						scenarioLabel: policy.scenario?.name,
						// Inline rate/limit objects
						rate: policy.rate ? {
							rateTypeEnum: policy.rate.rateTypeEnum,
							flatRateAmount: policy.rate.flatRateAmount,
							rateSteps: policy.rate.rateSteps
						} : undefined,
						overstayRate: policy.overstayRate ? {
							rateTypeEnum: policy.overstayRate.rateTypeEnum,
							flatRateAmount: policy.overstayRate.flatRateAmount,
							rateSteps: policy.overstayRate.rateSteps
						} : undefined,
						limit: policy.limit ? {
							minAmount: policy.limit.minAmount,
							maxAmount: policy.limit.maxAmount,
							minStayMinutes: policy.limit.minStayMinutes,
							maxStayMinutes: policy.limit.maxStayMinutes
						} : undefined,
						billingCycleId: policy.billingCycle?.billingCycleId
					}
				}
			});
		}
	};

	const columns: ColDef<PricingPolicyListNode>[] = useMemo(
		() => [
			{
				headerName: t('list.columns.name'),
				field: 'name',
				flex: 1,
				minWidth: 200
			},
			{
				headerName: t('list.columns.tariff', 'Tariff'),
				colId: 'tariffName',
				flex: 1,
				minWidth: 180,
				valueGetter: params =>
					tariffNameById.get(params.data?.tariffId ?? '') ?? '—'
			},
			{
				headerName: t('list.columns.scenario'),
				colId: 'scenarioDescription',
				flex: 1,
				minWidth: 160,
				valueGetter: params => params.data?.scenario?.name ?? '—'
			},
			{
				headerName: t('list.columns.primaryRate'),
				colId: 'rateType',
				flex: 1,
				minWidth: 160,
				valueGetter: params => {
					const rate = params.data?.rate;
					if (!rate) return '—';
					if (rate.rateTypeEnum === 'FLAT_RATE') return `Flat: €${Number(rate.flatRateAmount ?? 0).toFixed(2)}`;
					return 'Tiered';
				}
			},
			{
				headerName: t('list.columns.overstayRate'),
				colId: 'overstayRateType',
				flex: 1,
				minWidth: 160,
				valueGetter: params => {
					const rate = params.data?.overstayRate;
					if (!rate) return '—';
					if (rate.rateTypeEnum === 'FLAT_RATE') return `Flat: €${Number(rate.flatRateAmount ?? 0).toFixed(2)}`;
					return 'Tiered';
				}
			},
			{
				headerName: t('list.columns.limit'),
				colId: 'limitSummary',
				flex: 1,
				minWidth: 160,
				valueGetter: params => {
					const limit = params.data?.limit;
					if (!limit) return '—';
					const parts = [
						limit.minAmount != null ? `Min: €${Number(limit.minAmount).toFixed(2)}` : null,
						limit.maxAmount != null ? `Max: €${Number(limit.maxAmount).toFixed(2)}` : null
					].filter(Boolean);
					return parts.length > 0 ? parts.join(' / ') : '—';
				}
			},
			{
				headerName: t('list.columns.priority'),
				field: 'priority',
				width: 110,
				minWidth: 110,
				cellClass: 'ag-right-aligned-cell',
				headerClass: 'ag-right-aligned-header',
				valueFormatter: params => `${params.value ?? ''}`
			},
			{
				headerName: t('list.columns.status'),
				field: 'isActive',
				width: 120,
				minWidth: 120,
				cellRenderer: ActiveInactiveCellRenderer
			},
			...(showDeleteColumn ? [{
				headerName: '',
				field: undefined,
				width: 60,
				minWidth: 60,
				maxWidth: 60,
				cellRenderer: DeleteCellRenderer,
				cellRendererParams: {
					onDelete: handleDeletePricingPolicy,
					disabled: deleteLoading
				},
				sortable: false,
				filter: false,
				resizable: false
			}] : [])
		],
		[t, handleDeletePricingPolicy, deleteLoading, tariffNameById]
	);

	return (
		<ListPageLayout
			gridId="pricing-policies-list"
			columns={columns}
			data={allPolicies}
			title={t('list.title')}
			description={t('list.description')}
			searchPlaceholder={t('list.searchPlaceholder')}
			onSearchValueChange={setSearchValue}
			clearSearchToken={clearSearchToken}
			isLoading={isLoading || deleteLoading}
			onRowClick={handleRowClick}
			onCreate={goToCreate}
			onRefresh={handleRefresh}
			selectedItem={selectedPolicy}
			detailsTitle="Pricing Policy Details"
			onEdit={goToEdit}
			onDelete={handleDeletePricingPolicyById}
			onClone={handleClonePricingPolicy}
			onOpenInNewWindow={id => openEntityInNewWindow('pricingPolicies', id)}
			renderDetailsContent={(policy: PricingPolicyListNode) => (
				<PricingPolicyDetails
					pricingPolicyId={policy.pricingPolicyId}
				/>
			)}
			renderDetailsHeader={(policy, actions) => (
				<PricingPolicyDetailsHeader policy={policy} {...actions} />
			)}
			getRecordId={policy => policy.pricingPolicyId}
			showCreateButton={true}
			showRefreshButton={true}
			renderCustomFilters={() => (
				<div className="flex gap-2">
					<div className="w-44">
						<SentryDropdown
							options={tariffFilterOptions}
							value={selectedTariffId}
							onChange={value =>
								setSelectedTariffId(String(value))
							}
							placeholder={t('list.filters.allTariffs', 'All tariffs')}
							groupByCategory={false}
							className="w-full h-8 text-xs"
							variant="transparent"
						/>
					</div>
					{hasActiveFilters && (
						<ClearFiltersButton onClick={handleClearFilters} />
					)}
				</div>
			)}
			hasNextPage={hasNextPage}
			isLoadingMore={isLoadingMore}
			onLoadMore={handleLoadMore}
			entityName={t('entityName')}
		/>
	);
};
